import httpClient from 'hub-http/clients/apiClient';
import { ProductApiName } from 'merchandise-lib/constants/Product';
import { createRpcClientV2, readUnhandledEnumCase } from 'rpc-client-utils';
import {
  UpgradeConfigProduct,
  getUpgradeConfigWithPurchaseMotionsRpc,
} from '../../__generated__/chirp/com/hubspot/upgrade/management/rpc/UpgradeConfigInAppRpc';
import { RepInfo } from '../_core/common/constants/Types';

import { logRavenException } from '../_core/utils/errors';
import { trackApiFailure } from '../_core/utils/metricUtils';

const rpcClient = createRpcClientV2({ hubHttpClient: httpClient });

export enum UpgradePointStatus {
  STALE = 'STALE',
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
  PLANNED = 'PLANNED',
}

const upgradePointPromises: Record<
  string,
  undefined | Promise<UpgradeConfigProduct | void>
> = {};

// viewedUpgradePointsCache is a module cache that stores whether a user has
// viewed an upgrade point, via a unique upgradeKey. This allows to us to
// prevent usage trackers from firing more than once per app session.
export const viewedUpgradePointsCache: { [upgradeKey: string]: boolean } = {};

export const updateViewedUpgradePointsCache = (
  upgradeKey: string,
  onCacheUpdate?: () => void
) => {
  if (!viewedUpgradePointsCache[upgradeKey]) {
    viewedUpgradePointsCache[upgradeKey] = true;
    if (onCacheUpdate) onCacheUpdate();
  }
};

export function getApp() {
  return window.location.pathname.split('/')[1];
}

export function getScreen() {
  return window.location.pathname;
}

export function getHubAndTierFromApiName(apiName: ProductApiName) {
  const hub = apiName.split('-')[0];
  const tier = apiName.split('-')[2];

  return {
    hub: hub === 'crm' ? 'suite' : hub,
    tier: !tier && hub === 'hubspot' ? 'free' : tier, // free tools api name has no tier
  };
}

export function isCommerceHub(hub: string) {
  return hub === 'commerce';
}

export function fetchUpgradePoint({
  upgradeKey,
  shouldIncludePurchaseMotions = false,
  limit,
  amountUsed,
}: {
  upgradeKey: string;
  shouldIncludePurchaseMotions?: boolean;
  limit?: number;
  amountUsed?: number;
}): Promise<UpgradeConfigProduct | void> {
  if (!upgradeKey || upgradeKey === '') {
    return Promise.resolve();
  }

  return rpcClient
    .call(getUpgradeConfigWithPurchaseMotionsRpc, {
      request: { upgradeKey, shouldIncludePurchaseMotions, limit, amountUsed },
    })
    .then((response) => response.upgradeConfigProduct)
    .catch((error: any) => {
      const message =
        error?.cause?.status === 404
          ? `${upgradeKey}: upgrade point may not exist in the UMS.`
          : `${upgradeKey}: unexpected error fetching upgrade config`;

      logRavenException({
        customMessage: message,
        error,
        uniqueId: upgradeKey,
        fingerprint: ['fetchUpgradePoint'],
      });

      trackApiFailure({
        caller: 'fetchUpgradePoint',
        statusCode: error?.status ?? error?.cause?.status ?? 'unknown',
        upgradeKey,
      });
      return undefined;
    });
}

export function getUpgradePointPromise({
  upgradeKey,
}: {
  upgradeKey: string;
}): Promise<UpgradeConfigProduct | void> {
  if (upgradePointPromises[upgradeKey]) {
    return upgradePointPromises[upgradeKey]!;
  }

  const upgradePointPromise: Promise<UpgradeConfigProduct | void> = new Promise(
    (resolve) => {
      resolve(
        fetchUpgradePoint({
          upgradeKey,
          shouldIncludePurchaseMotions: false,
        })
      );
    }
  );

  upgradePointPromises[upgradeKey] = upgradePointPromise;
  return upgradePointPromise;
}

export const getRepName = (repInfo: RepInfo = {}) => {
  if (repInfo && repInfo.name) {
    return repInfo.name;
  }
  return 'Not assigned';
};

export function getUpgradeConfigTrackingProperties(
  upgradeConfig: UpgradeConfigProduct
) {
  return {
    app: getApp(),
    screen: getScreen(),
    feature: upgradeConfig.featureStoreFeature,
    upgradeKey: upgradeConfig.upgradeKey,
    pointType: upgradeConfig.pointType.toString(),
    apiName: upgradeConfig.bestFitApiNameForPurchase,
  };
}

export const getSource = (app: string, screen: string, uniqueId: string) =>
  `${app}-${screen}-general-${uniqueId}`;

export function getTrackingProperties(upgradeConfig: UpgradeConfigProduct) {
  const app = getApp();
  const screen = getScreen();
  const source = getSource(app, screen, upgradeConfig.upgradeKey);

  const { hub, tier } = getHubAndTierFromApiName(
    upgradeConfig.bestFitApiNameForPurchase as ProductApiName
  );

  return {
    app,
    screen,
    uniqueId: upgradeConfig.upgradeKey,
    apiName: upgradeConfig.bestFitApiNameForPurchase,
    source,
    upgradeKey: upgradeConfig.upgradeKey,
    pointType: readUnhandledEnumCase(upgradeConfig.pointType),
    feature: upgradeConfig.featureStoreFeature,
    hub,
    tier,
  };
}

export const warnArchivedUpgradePointUse = (upgradeKey: string) => {
  console.warn(
    `Upgrade point '${upgradeKey}' is archived, and should not be used in-app. If this upgrade point should not be archived, it can be restored through the UMS at https://tools.hubteamqa.com/ums/upgrade-points/${upgradeKey}.`
  );
};

/**
 * Unit and integration testing only
 */
function resetUpgradePointCaches() {
  Object.keys(upgradePointPromises).forEach((upgradeKey) => {
    delete upgradePointPromises[upgradeKey];
  });
  Object.keys(viewedUpgradePointsCache).forEach((upgradeKey) => {
    delete viewedUpgradePointsCache[upgradeKey];
  });
}

export const __TEST_UTILS__ = { resetUpgradePointCaches };

export type Lang =
  | 'da'
  | 'de'
  | 'en'
  | 'es'
  | 'fr'
  | 'it'
  | 'nl'
  | 'no'
  | 'pl'
  | 'pt'
  | 'pt-br'
  | 'fi'
  | 'sv'
  | 'zh'
  | 'zh-tw'
  | 'ja';
